'use client';

import type { BlogHeroProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { AllysBlogSearch } from './AllysBlogSearch';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysBlogHeroProps extends BlogHeroProps {
  messages?: Record<string, unknown>;
}

/**
 * AllysBlogHero - Allys-themed blog hero
 * Premium hero section with Allys design language
 */
export function AllysBlogHero({
  title,
  subtitle,
  backgroundImage,
  showSearch = false,
  onSearch,
  messages,
}: AllysBlogHeroProps) {
  const blogMsg = messages?.blog as Record<string, unknown> | undefined;
  const searchMsg = blogMsg?.search as Record<string, unknown> | undefined;
  return (
    <section className="relative py-28 lg:py-36 overflow-hidden [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
      {/* Background Image */}
      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <Image
            src={backgroundImage}
            alt="Blog hero background"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#243462]/80 to-[#243462]/60" />
          
          {/* Grain texture overlay */}
          <div 
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '180px 180px'
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={`text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight ${
              backgroundImage ? 'text-white' : 'text-[#243462]'
            }`}
            {...(backgroundImage && {
              style: {
                textShadow: '0 4px 60px rgba(0,0,0,0.5), 0 2px 30px rgba(0,0,0,0.4)'
              }
            })}
          >
            {title}
          </motion.h1>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`text-xl md:text-2xl mb-8 leading-relaxed font-light ${
                backgroundImage ? 'text-white/90' : 'text-gray-600'
              }`}
              {...(backgroundImage && {
                style: {
                  textShadow: '0 2px 30px rgba(0,0,0,0.5)'
                }
              })}
            >
              {subtitle}
            </motion.p>
          )}

          {/* Search Bar */}
          {showSearch && onSearch && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="max-w-2xl mx-auto mt-8"
            >
              <AllysBlogSearch
                onChange={onSearch}
                placeholder={safeGetString(searchMsg?.placeholder, 'Search articles...')}
                className="shadow-2xl"
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

